import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { peoplePulseDataService } from '../utils/peoplePulseDataService';
import '../styles/FeedbackRecognition.css';

function FeedbackRecognition() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('feedback');

  const [feedbackRequests, setFeedbackRequests] = useState([]);
  const [recognitions, setRecognitions] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const [showRequestFeedbackModal, setShowRequestFeedbackModal] = useState(false);
  const [showGiveFeedbackModal, setShowGiveFeedbackModal] = useState(false);
  const [showRecognitionModal, setShowRecognitionModal] = useState(false);
  const [selectedFeedbackRequest, setSelectedFeedbackRequest] = useState(null);

  const [feedbackRequestData, setFeedbackRequestData] = useState({
    recipients: [],
    questions: ['What am I doing well?', 'What could I improve?'],
    deadline: '',
    anonymous: false
  });

  const [feedbackResponseData, setFeedbackResponseData] = useState({
    responses: {}
  });

  const [recognitionData, setRecognitionData] = useState({
    recipientId: '',
    category: 'teamwork',
    message: '',
    isPublic: true
  });

  const [filters, setFilters] = useState({
    search: '',
    status: '',
    category: ''
  });

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [feedbackData, recognitionData] = await Promise.all([
        peoplePulseDataService.getFeedbackRequests(),
        peoplePulseDataService.getRecognitions()
      ]);

      setFeedbackRequests(feedbackData || []);
      setRecognitions(recognitionData || []);
      setError(null);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load feedback and recognition: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestFeedback = async (e) => {
    e.preventDefault();
    try {
      const newRequest = await peoplePulseDataService.requestFeedback(feedbackRequestData);
      setFeedbackRequests([newRequest, ...feedbackRequests]);
      setShowRequestFeedbackModal(false);
      resetFeedbackRequestForm();
      setSuccessMessage('Feedback requests sent successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to request feedback: ' + err.message);
    }
  };

  const handleGiveFeedback = async (e) => {
    e.preventDefault();
    try {
      await peoplePulseDataService.submitFeedback(
        selectedFeedbackRequest.id,
        feedbackResponseData
      );
      setShowGiveFeedbackModal(false);
      setSelectedFeedbackRequest(null);
      setFeedbackResponseData({ responses: {} });
      setSuccessMessage('Feedback submitted successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      loadAllData();
    } catch (err) {
      setError('Failed to submit feedback: ' + err.message);
    }
  };

  const handleGiveRecognition = async (e) => {
    e.preventDefault();
    try {
      const newRecognition = await peoplePulseDataService.giveRecognition(recognitionData);
      setRecognitions([newRecognition, ...recognitions]);
      setShowRecognitionModal(false);
      resetRecognitionForm();
      setSuccessMessage('Recognition sent successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to give recognition: ' + err.message);
    }
  };

  const addQuestion = () => {
    setFeedbackRequestData({
      ...feedbackRequestData,
      questions: [...feedbackRequestData.questions, '']
    });
  };

  const removeQuestion = (index) => {
    const newQuestions = feedbackRequestData.questions.filter((_, i) => i !== index);
    setFeedbackRequestData({ ...feedbackRequestData, questions: newQuestions });
  };

  const resetFeedbackRequestForm = () => {
    setFeedbackRequestData({
      recipients: [],
      questions: ['What am I doing well?', 'What could I improve?'],
      deadline: '',
      anonymous: false
    });
  };

  const resetRecognitionForm = () => {
    setRecognitionData({
      recipientId: '',
      category: 'teamwork',
      message: '',
      isPublic: true
    });
  };

  const openGiveFeedbackModal = (request) => {
    setSelectedFeedbackRequest(request);
    const initialResponses = {};
    request.questions.forEach((q, i) => {
      initialResponses[`q${i}`] = '';
    });
    setFeedbackResponseData({ responses: initialResponses });
    setShowGiveFeedbackModal(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': '#F59E0B',
      'completed': '#10B981',
      'partial': '#3B82F6',
      'expired': '#EF4444'
    };
    return colors[status] || '#6C757D';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'teamwork': '🤝',
      'innovation': '💡',
      'leadership': '⭐',
      'quality': '🏆',
      'dedication': '💪',
      'helpfulness': '❤️'
    };
    return icons[category] || '👏';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return formatDate(dateString);
  };

  if (loading) {
    return (
      <div className="hr-dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading feedback and recognition...</p>
      </div>
    );
  }

  const filteredFeedback = feedbackRequests.filter(request => {
    if (filters.search && !request.requestedBy?.toLowerCase().includes(filters.search.toLowerCase())) return false;
    if (filters.status && request.status !== filters.status) return false;
    return true;
  });

  const filteredRecognitions = recognitions.filter(recognition => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      if (!recognition.giverName?.toLowerCase().includes(searchLower) &&
          !recognition.recipientName?.toLowerCase().includes(searchLower) &&
          !recognition.message?.toLowerCase().includes(searchLower)) {
        return false;
      }
    }
    if (filters.category && recognition.category !== filters.category) return false;
    return true;
  });

  return (
    <div className="hr-dashboard-container peoplepulse-page">
      <div className="hr-dashboard-header">
        <div>
          <h1>Feedback & Recognition</h1>
          <p className="subtitle">Give and receive feedback, recognize great work</p>
        </div>
        <div className="header-actions">
          {activeTab === 'feedback' ? (
            <button className="btn-primary" onClick={() => setShowRequestFeedbackModal(true)}>
              + Request Feedback
            </button>
          ) : (
            <button className="btn-primary" onClick={() => setShowRecognitionModal(true)}>
              + Give Recognition
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <span>{successMessage}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs-container">
        <button
          className={`tab ${activeTab === 'feedback' ? 'active' : ''}`}
          onClick={() => setActiveTab('feedback')}
        >
          360° Feedback ({feedbackRequests.length})
        </button>
        <button
          className={`tab ${activeTab === 'recognition' ? 'active' : ''}`}
          onClick={() => setActiveTab('recognition')}
        >
          Recognition ({recognitions.length})
        </button>
      </div>

      {/* Feedback Tab */}
      {activeTab === 'feedback' && (
        <>
          {/* Filters */}
          <div className="filters-bar">
            <input
              type="text"
              placeholder="Search feedback..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="search-input"
            />

            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="partial">Partially Complete</option>
              <option value="completed">Completed</option>
              <option value="expired">Expired</option>
            </select>

            <button className="btn-secondary" onClick={() => setFilters({ search: '', status: '', category: '' })}>
              Clear Filters
            </button>
          </div>

          {/* Feedback List */}
          <div className="feedback-list">
            <div className="feedback-section">
              <h3>Feedback Requested by Me</h3>
              {filteredFeedback.filter(f => f.type === 'requested').length === 0 ? (
                <div className="empty-state">
                  <p>No feedback requests sent</p>
                </div>
              ) : (
                filteredFeedback.filter(f => f.type === 'requested').map((request) => (
                  <div key={request.id} className="feedback-request-card">
                    <div className="request-header">
                      <div>
                        <h4>Feedback Request</h4>
                        <span className="badge" style={{ backgroundColor: getStatusColor(request.status) }}>
                          {request.status}
                        </span>
                      </div>
                      <span className="request-date">Requested {formatRelativeTime(request.createdAt)}</span>
                    </div>

                    <div className="request-info">
                      <div className="info-item">
                        <label>Recipients</label>
                        <span>{request.totalRecipients || 0} people</span>
                      </div>
                      <div className="info-item">
                        <label>Responses</label>
                        <span>{request.responsesReceived || 0} of {request.totalRecipients || 0}</span>
                      </div>
                      <div className="info-item">
                        <label>Deadline</label>
                        <span>{formatDate(request.deadline)}</span>
                      </div>
                    </div>

                    <div className="request-actions">
                      <button className="btn-link" onClick={() => navigate(`/teamflect/feedback/${request.id}`)}>
                        View Responses
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="feedback-section">
              <h3>Feedback Requested from Me</h3>
              {filteredFeedback.filter(f => f.type === 'toRespond').length === 0 ? (
                <div className="empty-state">
                  <p>No pending feedback requests</p>
                </div>
              ) : (
                filteredFeedback.filter(f => f.type === 'toRespond').map((request) => (
                  <div key={request.id} className="feedback-request-card pending">
                    <div className="request-header">
                      <div>
                        <h4>Feedback for {request.requestedBy}</h4>
                        {request.anonymous && <span className="badge anonymous-badge">Anonymous</span>}
                      </div>
                      <span className="request-date">Due {formatDate(request.deadline)}</span>
                    </div>

                    <div className="questions-preview">
                      <label>Questions ({request.questions?.length || 0})</label>
                      <ul>
                        {request.questions?.slice(0, 2).map((q, i) => (
                          <li key={i}>{q}</li>
                        ))}
                        {request.questions?.length > 2 && <li>+ {request.questions.length - 2} more...</li>}
                      </ul>
                    </div>

                    <div className="request-actions">
                      <button className="btn-primary" onClick={() => openGiveFeedbackModal(request)}>
                        Provide Feedback
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}

      {/* Recognition Tab */}
      {activeTab === 'recognition' && (
        <>
          {/* Filters */}
          <div className="filters-bar">
            <input
              type="text"
              placeholder="Search recognition..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="search-input"
            />

            <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}>
              <option value="">All Categories</option>
              <option value="teamwork">Teamwork</option>
              <option value="innovation">Innovation</option>
              <option value="leadership">Leadership</option>
              <option value="quality">Quality</option>
              <option value="dedication">Dedication</option>
              <option value="helpfulness">Helpfulness</option>
            </select>

            <button className="btn-secondary" onClick={() => setFilters({ search: '', status: '', category: '' })}>
              Clear Filters
            </button>
          </div>

          {/* Recognition Feed */}
          <div className="recognition-feed">
            {filteredRecognitions.length === 0 ? (
              <div className="empty-state">
                <p>No recognitions yet</p>
                <button className="btn-primary" onClick={() => setShowRecognitionModal(true)}>
                  Give Recognition
                </button>
              </div>
            ) : (
              filteredRecognitions.map((recognition) => (
                <div key={recognition.id} className="recognition-card">
                  <div className="recognition-header">
                    <div className="recognition-icon">
                      {getCategoryIcon(recognition.category)}
                    </div>
                    <div className="recognition-info">
                      <h4>
                        <strong>{recognition.giverName}</strong> recognized{' '}
                        <strong>{recognition.recipientName}</strong>
                      </h4>
                      <span className="recognition-time">{formatRelativeTime(recognition.createdAt)}</span>
                    </div>
                    <span className="badge category-badge">{recognition.category}</span>
                  </div>

                  <div className="recognition-message">
                    <p>{recognition.message}</p>
                  </div>

                  {recognition.reactions && (
                    <div className="recognition-reactions">
                      <button className="reaction-btn">👏 {recognition.reactions.clap || 0}</button>
                      <button className="reaction-btn">❤️ {recognition.reactions.heart || 0}</button>
                      <button className="reaction-btn">🎉 {recognition.reactions.celebrate || 0}</button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* Request Feedback Modal */}
      {showRequestFeedbackModal && (
        <div className="modal-overlay" onClick={() => setShowRequestFeedbackModal(false)}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Request 360° Feedback</h2>
              <button onClick={() => { setShowRequestFeedbackModal(false); resetFeedbackRequestForm(); }}>×</button>
            </div>

            <form onSubmit={handleRequestFeedback}>
              <div className="form-group">
                <label>Who would you like to request feedback from? *</label>
                <input
                  type="text"
                  placeholder="Enter names or emails (comma separated)"
                  value={feedbackRequestData.recipients.join(', ')}
                  onChange={(e) => setFeedbackRequestData({
                    ...feedbackRequestData,
                    recipients: e.target.value.split(',').map(r => r.trim())
                  })}
                  required
                />
                <small>You can request feedback from peers, direct reports, or managers</small>
              </div>

              <div className="form-group">
                <label>Deadline *</label>
                <input
                  type="date"
                  value={feedbackRequestData.deadline}
                  onChange={(e) => setFeedbackRequestData({ ...feedbackRequestData, deadline: e.target.value })}
                  required
                />
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={feedbackRequestData.anonymous}
                    onChange={(e) => setFeedbackRequestData({ ...feedbackRequestData, anonymous: e.target.checked })}
                  />
                  Allow anonymous responses
                </label>
              </div>

              <div className="questions-section">
                <div className="section-header">
                  <h3>Questions</h3>
                  <button type="button" className="btn-secondary small" onClick={addQuestion}>
                    + Add Question
                  </button>
                </div>

                {feedbackRequestData.questions.map((question, index) => (
                  <div key={index} className="question-input-group">
                    <input
                      type="text"
                      value={question}
                      onChange={(e) => {
                        const newQuestions = [...feedbackRequestData.questions];
                        newQuestions[index] = e.target.value;
                        setFeedbackRequestData({ ...feedbackRequestData, questions: newQuestions });
                      }}
                      placeholder="Enter your question..."
                      required
                    />
                    <button
                      type="button"
                      className="btn-danger small"
                      onClick={() => removeQuestion(index)}
                      disabled={feedbackRequestData.questions.length === 1}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => { setShowRequestFeedbackModal(false); resetFeedbackRequestForm(); }}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Send Requests
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Give Feedback Modal */}
      {showGiveFeedbackModal && selectedFeedbackRequest && (
        <div className="modal-overlay" onClick={() => setShowGiveFeedbackModal(false)}>
          <div className="modal-content large-modal scrollable-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Feedback for {selectedFeedbackRequest.requestedBy}</h2>
              <button onClick={() => setShowGiveFeedbackModal(false)}>×</button>
            </div>

            <form onSubmit={handleGiveFeedback}>
              {selectedFeedbackRequest.questions.map((question, index) => (
                <div key={index} className="form-group">
                  <label>{question} *</label>
                  <textarea
                    value={feedbackResponseData.responses[`q${index}`] || ''}
                    onChange={(e) => setFeedbackResponseData({
                      responses: {
                        ...feedbackResponseData.responses,
                        [`q${index}`]: e.target.value
                      }
                    })}
                    rows="4"
                    required
                    placeholder="Your feedback..."
                  />
                </div>
              ))}

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowGiveFeedbackModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Submit Feedback
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Give Recognition Modal */}
      {showRecognitionModal && (
        <div className="modal-overlay" onClick={() => setShowRecognitionModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Give Recognition</h2>
              <button onClick={() => { setShowRecognitionModal(false); resetRecognitionForm(); }}>×</button>
            </div>

            <form onSubmit={handleGiveRecognition}>
              <div className="form-group">
                <label>Who would you like to recognize? *</label>
                <input
                  type="text"
                  value={recognitionData.recipientId}
                  onChange={(e) => setRecognitionData({ ...recognitionData, recipientId: e.target.value })}
                  required
                  placeholder="Enter name or email"
                />
              </div>

              <div className="form-group">
                <label>Category *</label>
                <select
                  value={recognitionData.category}
                  onChange={(e) => setRecognitionData({ ...recognitionData, category: e.target.value })}
                  required
                >
                  <option value="teamwork">🤝 Teamwork</option>
                  <option value="innovation">💡 Innovation</option>
                  <option value="leadership">⭐ Leadership</option>
                  <option value="quality">🏆 Quality Work</option>
                  <option value="dedication">💪 Dedication</option>
                  <option value="helpfulness">❤️ Helpfulness</option>
                </select>
              </div>

              <div className="form-group">
                <label>Message *</label>
                <textarea
                  value={recognitionData.message}
                  onChange={(e) => setRecognitionData({ ...recognitionData, message: e.target.value })}
                  rows="4"
                  required
                  placeholder="Describe what they did and why it matters..."
                />
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={recognitionData.isPublic}
                    onChange={(e) => setRecognitionData({ ...recognitionData, isPublic: e.target.checked })}
                  />
                  Make this recognition public
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => { setShowRecognitionModal(false); resetRecognitionForm(); }}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Send Recognition
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default FeedbackRecognition;

